import { GREEN } from "@finway-group/shared/lib/consts"
import { Employee } from "@finway-group/shared/lib/models"
import { Avatar, Skeleton, Tooltip } from "antd"
import React, { useEffect, useState } from "react"

import { doesImageExist } from "Shared/utils/helper.utils"

import { CrossRedCircled } from "./icons/CrossRedCircled"
import { TickGreenCircled } from "./icons/TickGreenCircled"

export type UserImageBadgeType = "success" | "failure"

type UserImageType = {
    user: Employee
    size?: "large" | "small" | number
    badge?: UserImageBadgeType
    heightPadding?: boolean
    isPersistent?: boolean
    tooltipTitle?: string
    showReplacementIcon?: boolean
}

export const UserImage = ({ user, size = "small", badge, heightPadding = true, isPersistent = false, tooltipTitle, showReplacementIcon = false }: UserImageType) => {
    const [imgLoading, setImgLoading] = useState(true)
    const [showInitials, setShowInitials] = useState(false)

    useEffect(() => {
        const checkImage = async () => {
            setImgLoading(true)
            const imageExists = await doesImageExist(user?.img)
            setImgLoading(false)
            setShowInitials(!imageExists)
        }
        void checkImage()
    }, [user?.img])

    if (!user) {
        return null
    }

    const getNameInitials = () => {
        const fullName = ""

        try {
            return user.getInitials().replace(" ", "")
        } catch (error) {
            return fullName
        }
    }

    const sizeInPx = size === "large" ? 40 : size === "small" ? 24 : size

    const onError = () => {
        setShowInitials(true)
        return false
    }

    const renderSuccessOrFailureBadge = (badgeType: UserImageBadgeType) =>
        badgeType === "success" ? (
            <TickGreenCircled size={10} className="absolute z-10" style={{ fontSize: 10, bottom: -2, right: -2 }} />
        ) : badgeType === "failure" ? (
            <CrossRedCircled size={10} className="absolute z-10" style={{ fontSize: 10, bottom: -2, right: -2 }} />
        ) : null

    const height = heightPadding ? sizeInPx + 8 : sizeInPx
    const width = sizeInPx + 14

    return (
        <div style={{ height, width }} className="flex flex-shrink-0 justify-center items-center">
            <div className="relative">
                {badge && renderSuccessOrFailureBadge(badge)}
                {showReplacementIcon && (
                    <img src={`/icons/replacement.svg`} alt="replacement" className="absolute z-10" style={{ top: sizeInPx / 2 - 8, left: -10, height: 16, width: 16 }} />
                )}
                {imgLoading ? (
                    <Skeleton.Avatar key={user?.id} className={`min-w-${sizeInPx} min-h-${sizeInPx}`} active={true} size={size} />
                ) : (
                    <Tooltip title={tooltipTitle} placement="top">
                        {showInitials ? (
                            <Avatar
                                key={user?.id}
                                size={size}
                                className={`${isPersistent ? "" : "animation-appear"} flex items-center self-center min-w-${sizeInPx} min-h-${sizeInPx}`}
                                style={{ color: GREEN, backgroundColor: "#ffffff" }}
                            >
                                {getNameInitials().trim()}
                            </Avatar>
                        ) : (
                            <Avatar key={user?.id} className={`min-w-${sizeInPx} min-h-${sizeInPx} bg-white`} size={size} src={user?.img} alt="User image" onError={onError} />
                        )}
                    </Tooltip>
                )}
            </div>
        </div>
    )
}

export default UserImage
